import { Injectable, NgZone } from '@angular/core';
import { filterNil, distinctUntilArrayItemChanged } from '@ngneat/elf';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Mutex } from 'async-mutex';
import { combineLatest, distinctUntilChanged, Subject, filter } from 'rxjs';
import { CapabilityTierService } from 'src/app/services/capability-tier.service';
import { AccessControl } from '@pncl/common-models';
import { INFOS } from '../common/utils/notification-constants';
import { AnalyticsEventType, AnalyticsInsightType } from '../models/analytics';
import { SpaceRepository } from '../state/space.repository';
import { ToasterPopupStyle } from '../ui/notification-toaster/custom-notification-toastr/custom-notification-toastr.component';
import { IconBackground } from '../ui/notification-toaster/icon-message-toaster-element/icon-message-toaster-element.component';
import { UserSpeakingLiveAnalyticsManager } from '../utilities/LiveAnalytics';
import { modifiedSetTimeout } from '../utilities/ZoneUtils';
import { CurrentlyDetectingAudio } from '../sessions/common/volume-detector';
import { InspectModeRepository } from '../state/inspect-mode-repository.service';
import { FLAGS, FlagsService } from './flags.service';
import { ForegroundActivityService } from './foreground-activity.service';
import {
  NotificationIconTypes,
  NotificationToasterService,
  NotificationType,
  NotificationActionButton,
  V2NotificationDataBuilder,
  NotificationIconConfig,
} from './notification-toaster.service';
import { ProviderStateService } from './provider-state.service';
import { SpacesService } from './spaces.service';
import { TelemetryService } from './telemetry.service';
import { TimerService } from './timer.service';
import { UserIdleService } from './user-idle.service';
import { UserService } from './user.service';
import { RtcServiceController } from './rtc.service';
import { TimeService } from './time.service';
import { CustomerSupportService } from './customer-support.service';
import { DeviceAndBrowserDetectorService } from './device-and-browser-detector.service';
import { AnalyticsEventsWindowEmitter } from './analytics-events-window-emitter.service';
import Capability = AccessControl.Capability;

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private analyticsEvents: Array<{
    eventType: AnalyticsEventType;
    timestamp: Date;
    roomUid?: string;
    userTabId: string;
    metadata?: Record<string, unknown>;
  }> = [];
  private currentSessionId?: string;
  private readonly analyticsMutex = new Mutex();
  batchingInterval = 10000; // default value
  spacesAnalyticsEnabled?: boolean;
  spaceAnalyticsInterval?: NodeJS.Timer;

  hiddenPropertyName?: string;
  visibilityChangePropertyName!: string;

  currentSessionId$: Subject<string> = new Subject<string>();
  getCurrentSessionId$: Subject<void> = new Subject<void>();

  // Saves insights on a room level
  realTimeInsights = new Map<string, Map<AnalyticsInsightType, number>>();

  private readonly CURRENT_SESSION_IDLE_THRESHOLD_SECONDS = 300; // 5 minutes

  // Speech analysis configuration
  private userSpeechDetectionManager?: UserSpeakingLiveAnalyticsManager;
  private userSpeechNotificationTimer?: NodeJS.Timer;

  // Default config values are set directly on properties
  private USER_SPEECH_LOWER_THRESHOLD = 0.3;
  private USER_SPEECH_UPPER_THRESHOLD = 0.8;
  private isSpeakingLowerThresholdNotificationEnabled = false;
  private isSpeakingMidThresholdNotificationEnabled = false;
  private isSpeakingUpperThresholdNotificationEnabled = false;
  private USER_SPEECH_NOTIFICATION_TIMER_DURATION = 30 * 60 * 1000; // 30 minutes
  private readonly SPEECH_TOASTR_NOTIFICATION_DURATION = 30;

  constructor(
    private readonly flagsService: FlagsService,
    private readonly spacesService: SpacesService,
    private readonly spaceRepo: SpaceRepository,
    private readonly userIdleService: UserIdleService,
    private readonly timerService: TimerService,
    private readonly userService: UserService,
    private readonly telemetry: TelemetryService,
    private readonly foregroundInactivityService: ForegroundActivityService,
    private readonly ngZone: NgZone,
    private readonly providerStateService: ProviderStateService,
    private readonly translateService: TranslateService,
    private readonly notificationToasterService: NotificationToasterService,
    private readonly rtcServiceController: RtcServiceController,
    private readonly timeService: TimeService,
    private readonly capabilityTierService: CapabilityTierService,
    private readonly customerSupportService: CustomerSupportService,
    private readonly deviceAndBrowserDetectorService: DeviceAndBrowserDetectorService,
    private readonly embedAnalyticsEventsService: AnalyticsEventsWindowEmitter,
    private readonly inspectModeRepository: InspectModeRepository,
  ) {
    this.timeService.generateClockSkewFromServer();

    if (this.inspectModeRepository.isInspectMode) {
      return;
    }
    
    // Ensures events are captured only when user is in a space
    combineLatest([
      this.spaceRepo.activeSpaceId$,
      this.flagsService.featureFlagChanged(FLAGS.SPACES_ANALYTICS_RECORDING), // in case feature flags are not initialised
    ])
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe(([spaceId, _]) => {
        this.analyticsMutex.runExclusive(async () => {
          if (spaceId) {
            if (this.spaceAnalyticsInterval) {
              return;
            }
            this.enableSpacesAnalytics();
          } else {
            await this.disableSpaceAnalytics();
          }
        });
      });

    // Initialize feature flag configuration values at startup
    this.initializeLiveCallAlertsConfiguration();

    // Setup user speech detection manager
    this.userSpeechDetectionManager = new UserSpeakingLiveAnalyticsManager(
      this.ngZone,
      this.telemetry,
      this,
      this.userService,
    );

    this.foregroundInactivityService.isForegroundInactive$
      .pipe(untilDestroyed(this))
      .subscribe((isInactive) => this.handleTabChange(isInactive));

    this.userIdleService
      .getUserIdleSubject(this.CURRENT_SESSION_IDLE_THRESHOLD_SECONDS)
      .then((isUserActiveSubject) => {
        isUserActiveSubject.pipe(untilDestroyed(this), distinctUntilChanged()).subscribe({
          next: (isUserActive) => {
            if (isUserActive) {
              // User is active (after inactivity)
              this.getCurrentSessionId$.next();
              this.addToAnalyticsEventsBatch(AnalyticsEventType.JOIN_SPACE);
            } else {
              const lastActiveTime = new Date(
                new Date().getTime() - this.CURRENT_SESSION_IDLE_THRESHOLD_SECONDS * 1000,
              );
              this.addToAnalyticsEventsBatch(AnalyticsEventType.LEAVE_SPACE, lastActiveTime);
              this.endSessionForUser();
            }
          },
          error: (err) => console.error(err),
        });
      });
  }

  private initializeDeviceInfoLogging(): void {
    // Listen to breakout room changes and log device info
    this.spaceRepo.activeSpaceCurrentRoomUid$
      .pipe(
        untilDestroyed(this),
        filter((roomUid) => !!roomUid),
        distinctUntilChanged(),
      )
      .subscribe((_) => {
        // Log device information when user changes breakout rooms
        this.logUserDeviceInfo();
      });
  }

  /**
   * Initialize configuration values from feature flags at startup
   * This only needs to be done once as these values don't typically change during runtime
   */
  private initializeLiveCallAlertsConfiguration(): void {
    // Subscribe to feature flag changes to update configuration when available
    this.flagsService
      .featureFlagChanged(FLAGS.LIVE_NOTIFICATION_SPEAKING_DURATION)
      .pipe(
        distinctUntilChanged(),
        filter((result) => result),
      )
      .subscribe(() => {
        this.updateLiveCallAlertsConfiguration();
      });
  }

  /**
   * Updates configuration based on current feature flag values
   * Called both at initialization and when feature flags change
   */
  private updateLiveCallAlertsConfiguration(): void {
    const speakingTimeNotificationVariables =
      this.flagsService.featureFlagsVariables[FLAGS.LIVE_NOTIFICATION_SPEAKING_DURATION];
    this.USER_SPEECH_LOWER_THRESHOLD =
      speakingTimeNotificationVariables.speaking_lower_threshold as number;
    this.USER_SPEECH_UPPER_THRESHOLD =
      speakingTimeNotificationVariables.speaking_upper_threshold as number;
    this.isSpeakingLowerThresholdNotificationEnabled =
      speakingTimeNotificationVariables.speaking_lower_threshold_notification_enabled as boolean;
    this.isSpeakingMidThresholdNotificationEnabled =
      speakingTimeNotificationVariables.speaking_mid_threshold_notification_enabled as boolean;
    this.isSpeakingUpperThresholdNotificationEnabled =
      speakingTimeNotificationVariables.speaking_upper_threshold_notification_enabled as boolean;
    if (speakingTimeNotificationVariables.call_duration_trigger_mins) {
      this.USER_SPEECH_NOTIFICATION_TIMER_DURATION =
        (speakingTimeNotificationVariables.call_duration_trigger_mins as number) * 60 * 1000;
    }

    // Setup activation tracking for live call alerts
    /**
     * Set up the reactive subscription that determines when live call alerts should be active
     * based on capability and institution settings
     */
    combineLatest([
      this.capabilityTierService.shouldEnableCapability$(Capability.LIVE_CALL_ALERTS),
      this.spaceRepo.activeSpaceId$.pipe(filterNil()),
      this.providerStateService.callConnected$,
      this.spaceRepo.isCurrentUserHost$,
    ])
      .pipe(untilDestroyed(this), distinctUntilArrayItemChanged())
      .subscribe(([isCapabilityEnabled, _, callConnected, isUserHost]) => {
        const institutionSettings = this.spaceRepo.activeSpace?.institution?.settings;
        if (
          callConnected &&
          isUserHost &&
          isCapabilityEnabled &&
          !!institutionSettings?.liveCallAlerts
        ) {
          this.telemetry.debugEvent('live_notification', { eventType: 'starting_timer' });
          this.userSpeechDetectionManager?.resetSpeechTime();
          this.userSpeechNotificationTimer = modifiedSetTimeout(() => {
            this.promptUserSpeakingNotifications();
          }, this.USER_SPEECH_NOTIFICATION_TIMER_DURATION);
        } else {
          if (this.userSpeechNotificationTimer) {
            this.telemetry.debugEvent('live_notification', { eventType: 'clear_timer' });
            clearTimeout(this.userSpeechNotificationTimer);
          }
          this.userSpeechDetectionManager?.unsubscribe();
        }
      });
  }

  /**
   * Starts analytics interval which saves events
   */
  enableSpacesAnalytics(): void {
    if (this.inspectModeRepository.isInspectMode) {
      return;
    }
    if (this.spaceAnalyticsInterval) {
      return;
    }

    this.spacesAnalyticsEnabled = this.flagsService.isFlagEnabled(FLAGS.SPACES_ANALYTICS_RECORDING);
    if (!this.spacesAnalyticsEnabled) {
      return;
    }
    this.batchingInterval = this.flagsService.featureFlagsVariables[
      FLAGS.SPACES_ANALYTICS_RECORDING
    ].batching_interval_msecs as number;

    if (this.spacesAnalyticsEnabled) {
      this.ngZone.runOutsideAngular(async () => {
        // Do not log events if running FE locally
        if (window.location.hostname === 'localhost') {
          return;
        }
        this.spaceAnalyticsInterval = await this.timerService.setInterval(() => {
          this._saveRealTimeEvents();
        }, this.batchingInterval);

        // This call will capture the device info on the first space load
        // It's called here to be sure all variables are set
        // It will capture the device info on BR changes as well
        this.initializeDeviceInfoLogging();
      });
    }
  }

  /**
   * Stops analytics interval which saves events
   */
  async disableSpaceAnalytics() {
    if (this.spaceAnalyticsInterval) {
      this._saveRealTimeEvents();
      await this.timerService.clearInterval(this.spaceAnalyticsInterval);
      this.spaceAnalyticsInterval = undefined;
    }
  }

  addToAnalyticsEventsBatch(
    eventType: AnalyticsEventType,
    timestamp?: Date,
    metadata?: Record<string, unknown>,
  ): void {
    this.embedAnalyticsEventsService.emit(eventType);

    if (!this.spacesAnalyticsEnabled || !this.spaceAnalyticsInterval) {
      return;
    }
    timestamp = this.timeService.date(timestamp);
    const currentRoomUid = this.spaceRepo.activeSpaceCurrentRoomUid;
    if (!currentRoomUid) {
      this.telemetry.debugEvent('missing_roomId_analytics_event', {
        eventType,
      });
      return;
    }
    this.analyticsEvents.push({
      eventType,
      timestamp,
      roomUid: currentRoomUid,
      userTabId: this.userService.userUniqueHash,
      metadata,
    });
  }

  private _saveRealTimeEvents() {
    if (this.inspectModeRepository.isInspectMode) {
      return;
    }
    if (
      (this.analyticsEvents.length > 0 || this.realTimeInsights.size > 0) &&
      this.currentSessionId
    ) {
      const events = this.analyticsEvents.splice(0, this.analyticsEvents.length);
      const insights = [...this.realTimeInsights.entries()].map(([roomUid, roomInsights]) => ({
        roomUid,
        roomInsights: Object.fromEntries(roomInsights),
      }));
      this.spacesService.sendAnalyticsEvents(this.currentSessionId, events, insights)?.subscribe();
      this.realTimeInsights.clear();
    }
  }

  getCurrentSessionId(): string | undefined {
    return this.currentSessionId;
  }

  setCurrentSessionId(id: string): void {
    if (id === this.currentSessionId) {
      return;
    }
    this._saveRealTimeEvents();
    this.currentSessionId = id;
    this.currentSessionId$.next(id);

    // This call won't capture the first space load as other variables ex: spaceAnalyticsInterval are not set yet
    // But it will capture the device info when the session changes in real time ex: end a session manually
    if (id) {
      this.logUserDeviceInfo();
    }
  }

  private logUserDeviceInfo(): void {
    const deviceInfo = {
      deviceType: this.deviceAndBrowserDetectorService.getCurrentDeviceType(),
      browser: this.deviceAndBrowserDetectorService.getCurrentBrowser(),
      userAgent: this.deviceAndBrowserDetectorService.getUserAgent(),
    };

    this.addToAnalyticsEventsBatch(AnalyticsEventType.USER_DEVICE_INFO, undefined, deviceInfo);
  }

  private handleTabChange(isInactive: boolean) {
    if (isInactive) {
      this.addToAnalyticsEventsBatch(AnalyticsEventType.HIDE_TAB);
    } else {
      this.addToAnalyticsEventsBatch(AnalyticsEventType.UNHIDE_TAB);
    }
  }

  endSessionForUser() {
    this.setCurrentSessionId('');
  }

  getSpeechDetectionSubject(userId: string): Subject<CurrentlyDetectingAudio> | undefined {
    // Only return the subject if the user is the current user (for speech logging)
    // or if the live notification is enabled
    if (this.isSpeakingTimeLiveNotificationEnabled() || userId === this.userService.userId) {
      return this.userSpeechDetectionManager?.getUserSubject(userId);
    }
  }

  private promptUserSpeakingNotifications() {
    if (!this.userService.userId || !this.userSpeechDetectionManager) {
      return;
    }
    this.telemetry.debugEvent('live_notification', {
      eventType: 'prompt_notification',
    });
    const userSpeechRatio = this.userSpeechDetectionManager.getUserSpeakingRatio(
      this.userService.userId,
    );
    let notificationTitle: string;
    let notificationBody = `${this.translateService.instant('In the last')} ${
      this.USER_SPEECH_NOTIFICATION_TIMER_DURATION / 60000
    } ${this.translateService.instant("mins, you've")} ${
      userSpeechRatio < this.USER_SPEECH_LOWER_THRESHOLD
        ? this.translateService.instant('only')
        : ''
    } ${this.translateService.instant('spoken for')} <strong>${Math.round(
      userSpeechRatio * 100,
    )}%</strong> ${this.translateService.instant('of the time.')} `;
    let isNotificationEnabled;
    if (userSpeechRatio >= this.USER_SPEECH_UPPER_THRESHOLD) {
      notificationTitle = this.translateService.instant('Why not let others speak a bit?');
      notificationBody += this.translateService.instant('Consider letting others speak more.');
      isNotificationEnabled = this.isSpeakingLowerThresholdNotificationEnabled;
    } else if (
      userSpeechRatio > this.USER_SPEECH_LOWER_THRESHOLD &&
      userSpeechRatio < this.USER_SPEECH_UPPER_THRESHOLD
    ) {
      notificationTitle = this.translateService.instant("You're a team player!");
      notificationBody += this.translateService.instant('Nice work letting others speak! ');
      isNotificationEnabled = this.isSpeakingMidThresholdNotificationEnabled;
    } else {
      notificationTitle = this.translateService.instant('Why not speak more?');
      notificationBody += this.translateService.instant('Try speaking a bit more!');
      isNotificationEnabled = this.isSpeakingUpperThresholdNotificationEnabled;
    }
    if (!isNotificationEnabled) {
      return;
    }

    const notificationIcon: NotificationIconConfig = {
      assetName: 'record_voice_over',
      type: NotificationIconTypes.MAT_ICON,
      background: IconBackground.INFO,
      size: 16,
      outlined: true,
    };
    const actionLinkButton: NotificationActionButton = {
      text: this.translateService.instant('Learn More'),
      onClick: () => {
        this.customerSupportService.openLink(
          'https://helpdesk.pencilspaces.com/en/articles/9073480-how-do-live-call-alerts-work',
        );
      },
      close: true,
    };

    this.notificationToasterService.showNotification(
      new V2NotificationDataBuilder(INFOS.LIVE_NOTIFICATION_SPEECH)
        .style(ToasterPopupStyle.WARN)
        .timeOut(this.SPEECH_TOASTR_NOTIFICATION_DURATION)
        .type(NotificationType.WARNING)
        .title(notificationTitle)
        .description(notificationBody)
        .icon(notificationIcon)
        .actionButtonsConfig({
          link: actionLinkButton,
        })
        .extendedTimeout(this.SPEECH_TOASTR_NOTIFICATION_DURATION)
        .dismissable(true)
        .priority(790)
        .build(),
    );
  }

  isSpeakingTimeLiveNotificationEnabled(): boolean {
    return (
      this.capabilityTierService.shouldEnableCapability(Capability.LIVE_CALL_ALERTS) &&
      !!this.spaceRepo.activeSpace?.institution?.settings?.liveCallAlerts
    );
  }

  setRealTimeInsight(insight: AnalyticsInsightType, value: number) {
    const roomUid = this.spaceRepo.activeSpaceCurrentRoom?.uid;
    if (!roomUid) {
      return;
    }
    if (!this.realTimeInsights.get(roomUid)) {
      this.realTimeInsights.set(roomUid, new Map());
    }
    const roomInsights = this.realTimeInsights.get(roomUid)!;
    roomInsights.set(insight, (roomInsights.get(insight) ?? 0) + value);
  }

  handleManualEndSession(data: { newSessionId: string; oldSessionId: string }): void {
    if (data.newSessionId) {
      this.setCurrentSessionId(data.newSessionId);
    }
    // If the session was manually ended by user/API, we need to check if the user was muted/unmuted
    // If the user was unmuted, we need to log these events so the analytics computation is accurate
    if (data.oldSessionId) {
      if (this.rtcServiceController.service.isCameraEnabled()) {
        this.addToAnalyticsEventsBatch(AnalyticsEventType.UNMUTE_VIDEO);
      }
      if (this.rtcServiceController.service.isMicEnabled()) {
        this.addToAnalyticsEventsBatch(AnalyticsEventType.UNMUTE_AUDIO);
      }
    }
  }
}
